/* SPDX-License-Identifier: AGPL-3.0-or-later */

import { Component, computed, inject, input, model, output, Signal } from '@angular/core';
import { MatIcon } from '@angular/material/icon';
import { MatButtonModule, MatIconButton } from '@angular/material/button';
import { MatTooltip } from '@angular/material/tooltip';
import { NgClass } from '@angular/common';
import {
  TeamDto,
  TournamentQualificationMatchDto,
  TournamentQualificationMatchParticipantDto,
} from '@api';
import { TournamentDialogsService } from '../../../services/tournament-dialogs.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { WatchTimerComponent } from '../../watch-timer/watch-timer.component';

@Component({
  selector: 'app-qualification-match-line',
  standalone: true,
  imports: [MatButtonModule, MatIcon, MatIconButton, MatTooltip, NgClass, WatchTimerComponent],
  templateUrl: './qualification-match-line.component.html',
  styleUrl: './qualification-match-line.component.scss',
})
export class QualificationMatchLineComponent {
  private tournamentDialogs = inject(TournamentDialogsService);
  private snackBar = inject(MatSnackBar);

  tournamentId = input<number>();
  teams = input<Map<number, TeamDto>>();
  viewOnly = input<boolean>(false);
  disableMatchEdit = input<boolean>(false);
  match = model<TournamentQualificationMatchDto>({});

  onMatchUpdate = output<TournamentQualificationMatchDto>();
  markTeamDrinksPickedUp = output<TournamentQualificationMatchParticipantDto>();

  participant1: Signal<TournamentQualificationMatchParticipantDto | undefined> = computed(() =>
    this.match()?.participants ? this.match()?.participants![0] : undefined,
  );
  participant2: Signal<TournamentQualificationMatchParticipantDto | undefined> = computed(() =>
    this.match()?.participants ? this.match()?.participants![1] : undefined,
  );

  get matchHasStarted(): boolean {
    return !!this.match().startTime;
  }

  participantIsCurrentlyPlaying(
    participant: TournamentQualificationMatchParticipantDto | undefined,
  ): boolean {
    if (participant && participant.teamId) {
      return !!this.teams()?.get(participant.teamId)?.currentlyPlaying;
    }
    return false;
  }

  async openMatchResultDialog() {
    const results = await this.tournamentDialogs.openQualificationMatchResultDialog({
      tournamentId: this.tournamentId()!,
      matchId: this.match()!.id!,
      participant1: this.participant1()!,
      participant2: this.participant2()!,
      previousResults: this.match(),
    });

    if (results) {
      this.snackBar.open('Match results updated', 'Close', { duration: 3000 });
      this.match.update(() => results);
      this.onMatchUpdate.emit(results);
    }
  }

  get matchResultString(): string {
    if (this.participant1()?.isWinner) {
      return `${this.match()?.winnerPoints} - 0`;
    } else {
      return `0 - ${this.match()?.winnerPoints}`;
    }
  }

  get resultTooltip(): string {
    if (!this.viewOnly()) {
      return this.matchHasStarted ? 'Enter match result' : 'Match has not started yet!';
    } else {
      return '';
    }
  }

  calculateTimeSpent(): string {
    if (this.match()?.endTime) {
      const elapsed = new Date(
        new Date(this.match()!.endTime!).getTime() - new Date(this.match()!.startTime!).getTime(),
      );
      const hours = Math.floor(elapsed.getTime() / (1000 * 60 * 60));
      const minutes = elapsed.getUTCMinutes();
      const seconds = elapsed.getUTCSeconds();
      const hoursStr = hours.toString().padStart(2, '0');
      const minutesStr = minutes.toString().padStart(2, '0');
      const secondsStr = seconds.toString().padStart(2, '0');
      return hoursStr !== '00'
        ? `${hoursStr}:${minutesStr}:${secondsStr}`
        : `${minutesStr}:${secondsStr}`;
    }
    return '';
  }
}
